/**
 * HTML5 Video Component
 *
 * The **HTML5 Video** component is an Alpine.js utility designed to enhance native HTML5 <video> elements with essential
 * functionality for modern web development. It provides support for key features like lazy loading, autoplay control,
 * and reduced motion preferences, while remaining lightweight and straightforward to use.
 *
 * ### Minimal Usage:
 * ```html
 * <div x-data="html5Video">
 *   <video autoplay loop muted playsinline>...</video>
 * </div>
 * ```
 *
 * @typedef {Object} HTML5VideoComponent
 * @property {Boolean} hasEnded - Indicates whether the video has finished playback.
 * @property {Boolean} isMuted - Indicates whether the video is muted.
 * @property {Boolean} isPlaying - Indicates whether the video is currently playing.
 * @property {Boolean} isReady - Indicates whether the video is ready for playback.
 * @property {Boolean|null} prefersReducedMotion - Reflects the reduced motion preference (`null` until initialized).
 * @property {HTMLElement|null} videoElement - The controlled `<video>` element or `null` if not found.
 * @property {Function} play - Starts video playback.
 * @property {Function} pause - Pauses video playback.
 * @property {Function} playFromStart - Restarts video playback from the beginning.
 * @property {Function} pauseFromStart - Pauses and resets video playback to the beginning.
 * @property {Function} lazyload - Loads the video on demand. Accepts a `Boolean` to control autoplay.
 * @property {Function} toggleMute - Toggles mute state of video.
 * @property {Function} init - Initializes the component and sets up listeners.
 * @property {Function} destroy - Cleans up listeners and state when the component is destroyed.
 *
 * @typedef {Object} HTML5VideoOptions
 * @property {string|null} [refName=null] - The name of the x-ref for the video element.
 *   If `null`, the component will attempt to find the video element via `querySelector`.
 * @property {Function|Boolean|null} [prefersReducedMotion=null] - Optional override for the `prefers-reduced-motion` behavior.
 *   - A `Function`, e.g. `() => $store.prefersReducedMotion`
 *   - If a `Boolean`, it directly sets the reduced motion preference.
 *   - If a `MediaQueryList`, we match to that (default)
 *
 * @param {HTML5VideoOptions|null} [options={}] - Additional options to configure the component.
 * @returns {HTML5VideoComponent} - An object containing methods and state for managing the video component.
 */
export default function HTML5Video(options = {}) {

  /**
   * Destructured options object.
   * @type {HTML5VideoOptions}
   */
  const {
    refName: _refName = null,
    prefersReducedMotion: _prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)'),
  } = options ?? {};

  /**
   * Resets the video element by removing all listeners.
   */
  const _removeVideoElementListeners = () => {
    if (_videoElement && _listeners) {
      for (const [event, handler] of Object.entries(_listeners)) {
        _videoElement.removeEventListener(event, handler);
      }
    }
  };

  /**
   * A map of event listeners attached to the video element.
   * @type {Object<string, Function>}
   */
  let _listeners = {};

  /**
   * The listener for changes to prefers-reduced-motion.
   * @type {Function|null}
   */
  let _mediaQueryListener = null;

  /**
   * The video element being controlled by this component.
   * @type {HTMLVideoElement|null}
   */
  let _videoElement = null;

  /**
   * The reactive state and public methods of the Alpine.js component.
   */
  return {

    /**
     * @typedef {Object} AlpineContext - https://alpinejs.dev/
     * @property {Function} $nextTick - Runs a callback after the DOM updates.
     * @property {Object} $refs - A map of DOM elements referenced via `x-ref`.
     */

    /**
     * Indicates whether the video has ended playback
     * @type {Boolean}
     * */
    hasEnded : false,

    /**
     * Indicates whether the video is currently muted
     * @type {Boolean|null}
     * */
    isMuted : null,

    /**
     * Indicates whether the video is currently playing
     * @type {Boolean}
     * */
    isPlaying : false,

    /**
     * Indicates whether the video is ready to play
     * @type {Boolean}
     */
    isReady : false,

    /**
     * Indicates whether the user prefers reduced motion
     * Starts as `null` so that we know when it's been set for the first time.
     * @type {Boolean|null}
     */
    prefersReducedMotion : null,

    /**
     * Getter of our `<video>` element
     * @returns {HTMLVideoElement|null}
     */
    get videoElement() {
      return _videoElement;
    },

    /**
     * Setter of our `<video>` element
     * @param {HTMLElement|null} el
     */
    set videoElement(el) {
      // pass it on to our private variable
      _videoElement = el;
      if (!_videoElement) {
        console.info(_refName
          ? `Video element not found for x-ref '${_refName}'`
          : 'No <video> element found within the component.');
        return;
      }

      // Attach all html5 video listeners
      for (const [event, handler] of Object.entries(_listeners)) {
        _videoElement.addEventListener(event, handler);
      }

      // Synchronize initial state
      this.hasEnded = false;
      this.isPlaying = !_videoElement.paused && !_videoElement.ended && _videoElement.readyState > 2;
      this.isReady = _videoElement.readyState >= 3;
      this.isMuted = _videoElement.muted;

      // Disable autoplay if `prefers-reduced-motion` is enabled
      if (this.prefersReducedMotion && (_videoElement.autoplay || this.isPlaying)) {
        this.pauseFromStart();
      }

      // Initiate playback if `prefers-reduced-motion` is disabled and video should autoplay
      if (!this.prefersReducedMotion && _videoElement.autoplay && !this.isPlaying) {
        this.playFromStart();
      }
    },

    /**
     * Starts video playback.
     */
    play() {
      this.$nextTick(() => {
        if (!this.videoElement) {
          console.info('Attempted to call play(), but videoElement is not defined.');
          return;
        }
        this.videoElement.play().catch(e => console.info(e));
      });
    },

    /**
     * Pauses video playback.
     */
    pause() {
      this.$nextTick(() => {
        if (!this.videoElement) {
          console.info('Attempted to call pause(), but videoElement is not defined.');
          return;
        }
        this.videoElement.pause();
      });
    },

    /**
     * Restarts video playback from the beginning.
     */
    playFromStart() {
      this.$nextTick(() => {
        if (!this.videoElement) {
          console.info('Attempted to call playFromStart(), but videoElement is not defined.');
          return;
        }
        this.videoElement.currentTime = 0;
        this.videoElement.play().catch(e => console.info(e));
      });
    },

    /**
     * Pauses and resets the video playback to the beginning.
     */
    pauseFromStart() {
      this.$nextTick(() => {
        if (!this.videoElement) {
          console.info('Attempted to call pauseFromStart(), but videoElement is not defined.');
          return;
        }
        this.videoElement.pause();
        this.videoElement.currentTime = 0;
      });
    },

    /**
     * Toggles the mute state of the video.
     */
    toggleMute() {
      this.$nextTick(() => {
        if (!this.videoElement) {
          console.info('Attempted to call toggleMute(), but videoElement is not defined.');
          return;
        }
        this.isMuted = !this.videoElement.muted;
        this.videoElement.muted = this.isMuted;
      });
    },

    /**
     * (Lazy)loads a video on demand.
     *
     * Use in combination with `x-intersect` e.g. `x-intersect="lazyload()"`
     * To lazyload a video, do not include `autoload`, instead add `preload="none"`. No other changes are necessary.
     * `false` can be passed to `lazyload()` to load the video but not autoplay.
     *
     * @param {Boolean} autoplay Whether to autoplay or just load the video
     */
    lazyload(autoplay = true) {
      this.$nextTick(() => {
        if (!this.videoElement) {
          console.info('Attempted to lazyload video, but videoElement is not defined.');
          return;
        }

        // Only run once
        if (this.isReady) return;

        // Only load the video if `prefers-reduced-motion` is enabled or autoplay is false
        if (this.prefersReducedMotion || !autoplay) {
          this.videoElement.load();
        }

        // Force playback if `prefers-reduced-motion` is disabled and video should autoplay
        if (!this.prefersReducedMotion && autoplay && !this.isPlaying) {
          this.playFromStart();
        }
      });
    },

    /**
     * Initializes the component and sets up event listeners.
     */
    init() {

      // When prefers-reduced-motion changes, set/reset videoElement
      this.$watch('prefersReducedMotion', () => {
        this.$nextTick(() => {
          if (this.videoElement) {
            _removeVideoElementListeners();
          }
          this.videoElement = _refName ? this.$refs[_refName] : this.$el.querySelector('video');
        });
      });

      // Define our html5 video listener callbacks
      _listeners = {
        play: () => {
          this.hasEnded = false;
          this.isPlaying = true;
          },
        pause: () => { this.isPlaying = false; },
        ended: () => {
          this.hasEnded = true;
          this.isPlaying = false;
        },
        canplay: () => { this.isReady = true; },
        emptied: () => { this.isReady = false; },
        volumechange: () => {
          this.isMuted = this.videoElement.muted;
        },
        error: (e) => { console.error('Video error:', e); },
      };

      // Determine `this.prefersReducedMotion` from a media query
      if (_prefersReducedMotion instanceof MediaQueryList) {
        // Define our `prefers-reduced-motion` media query listener callback
        _mediaQueryListener = (event) => {
          // setting prefersReducedMotion will trigger videoElement to be re-set
          this.prefersReducedMotion = event.matches;
        };

        // Listen for changes to `prefers-reduced-motion`
        _prefersReducedMotion.addEventListener('change', _mediaQueryListener);

        // Set prefersReducedMotion for the first time in component lifecycle
        this.prefersReducedMotion = _prefersReducedMotion.matches;

        // Happy return
        return;
      }

      // Determine `this.prefersReducedMotion` from a function e.g. `() => $store.prefersReducedMotion`
      if (typeof _prefersReducedMotion === 'function') {

        // Watch for changes to function
        this.$watch(_prefersReducedMotion, (newValue) => {
          this.prefersReducedMotion = newValue;
        });

        // Set prefersReducedMotion for the first time in component lifecycle
        this.prefersReducedMotion = _prefersReducedMotion();

        // Happy return
        return;
      }

      // Determine `this.prefersReducedMotion` from a boolean value
      if (typeof _prefersReducedMotion === 'boolean') {
        // Set prefersReducedMotion for the first (and only) time in component lifecycle
        this.prefersReducedMotion = _prefersReducedMotion;

        // Happy return
        return;
      }

      console.warn(
        'Invalid value for prefersReducedMotion. Expected a function, boolean, or MediaQueryList.'
      );
    },

    /**
     * Cleans up event listeners when the component is destroyed.
     */
    destroy() {
      // Remove  `prefers-reduced-motion` media query listener
      if (_prefersReducedMotion instanceof MediaQueryList && _mediaQueryListener) {
        _prefersReducedMotion.removeEventListener('change', _mediaQueryListener);
      }

      // Remove html5 video event listeners
      _removeVideoElementListeners();
    },
  };
}
